
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  CalendarDays, 
  User, 
  MapPin, 
  Wrench, 
  Clock, 
  AlertCircle, 
  Trash2, 
  Edit, 
  Building2, 
  UserCircle 
} from "lucide-react";

interface TaskCardProps {
  task: {
    id: string;
    title: string;
    status: "pending" | "in-progress" | "completed" | "deleted";
    assignedTo: string;
    date: string;
    location: string;
    priority: "low" | "medium" | "high";
    estimatedHours: number;
    taskType: "installation" | "repair" | "inspection" | "maintenance";
    createdByType: "admin" | "employee";
    deleted_at?: string;
    deleted_by?: string;
    deleted_by_name?: string;
  };
  onEdit: (taskId: string) => void;
  onDelete: (taskId: string) => void;
}

const TaskCard = ({ task, onEdit, onDelete }: TaskCardProps) => {
  const statusColors = {
    pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    "in-progress": "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-green-100 text-green-800 border-green-200",
    deleted: "bg-red-100 text-red-800 border-red-200",
  };

  const priorityColors = {
    low: "bg-blue-100 text-blue-800 border-blue-200",
    medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
    high: "bg-red-100 text-red-800 border-red-200",
  };

  const statusText = {
    pending: "Afventer",
    "in-progress": "I gang",
    completed: "Afsluttet",
    deleted: "Slettet"
  };

  const priorityText = {
    low: "Lav prioritet",
    medium: "Medium prioritet",
    high: "Høj prioritet"
  };

  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case "installation":
        return Wrench;
      case "repair":
        return AlertCircle;
      case "inspection":
        return Clock;
      case "maintenance":
        return Wrench;
      default:
        return Wrench;
    }
  };

  const TaskTypeIcon = getTaskTypeIcon(task.taskType);

  return (
    <Card 
      className="p-4 hover:shadow-lg transition-shadow border-2 shadow-md bg-white relative z-10 backdrop-blur-none isolate"
      role="article"
      aria-labelledby={`task-title-${task.id}`}
    >
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <TaskTypeIcon 
            className="w-5 h-5 text-gray-700" 
            aria-hidden="true"
          />
          <h3 
            id={`task-title-${task.id}`}
            className="font-semibold text-lg text-gray-900"
          >
            {task.title}
          </h3>
        </div>
        <div className="flex gap-2">
          <Badge 
            className={`${priorityColors[task.priority]} border font-medium`}
            aria-label={priorityText[task.priority]}
          >
            {task.priority}
          </Badge>
          <Badge 
            className={`${statusColors[task.status]} border font-medium`}
            aria-label={statusText[task.status]}
          >
            {statusText[task.status]}
          </Badge>
        </div>
      </div>
      
      <div className="space-y-2 text-gray-700">
        <div className="flex items-center gap-2 border border-gray-200 p-2 rounded-md shadow-sm bg-white backdrop-blur-none">
          <User className="w-4 h-4" aria-hidden="true" />
          <span className="font-medium text-gray-900">Tildelt til: {task.assignedTo}</span>
        </div>
        <div className="flex items-center gap-2 border border-gray-200 p-2 rounded-md shadow-sm bg-white backdrop-blur-none">
          <CalendarDays className="w-4 h-4" aria-hidden="true" />
          <span className="font-medium text-gray-900">Dato: {task.date}</span>
        </div>
        <div className="flex items-center gap-2 border border-gray-200 p-2 rounded-md shadow-sm bg-white backdrop-blur-none">
          <MapPin className="w-4 h-4" aria-hidden="true" />
          <span className="font-medium text-gray-900">Lokation: {task.location}</span>
        </div>
        <div className="flex items-center gap-2 border border-gray-200 p-2 rounded-md shadow-sm bg-white backdrop-blur-none">
          <Clock className="w-4 h-4" aria-hidden="true" />
          <span className="font-medium text-gray-900">Estimeret tid: {task.estimatedHours} timer</span>
        </div>
        {task.deleted_at && task.deleted_by_name && (
          <div className="flex items-center gap-2 border border-gray-200 p-2 rounded-md shadow-sm bg-white backdrop-blur-none">
            <Trash2 className="w-4 h-4 text-red-600" aria-hidden="true" />
            <span className="font-medium text-red-600">
              Slettet af {task.deleted_by_name} den {new Date(task.deleted_at).toLocaleString('da-DK')}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 mt-2 p-2 rounded-md border border-gray-200 shadow-sm bg-white backdrop-blur-none">
          {task.createdByType === 'admin' ? (
            <>
              <Building2 className="w-4 h-4 text-blue-600" aria-hidden="true" />
              <span className="text-blue-600 font-medium">Admin-oprettet</span>
            </>
          ) : (
            <>
              <UserCircle className="w-4 h-4 text-green-600" aria-hidden="true" />
              <span className="text-green-600 font-medium">Medarbejder-oprettet</span>
            </>
          )}
        </div>
      </div>

      {task.status !== 'deleted' && (
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(task.id)}
            className="text-blue-600 hover:text-blue-700 border-blue-200 hover:bg-blue-50 bg-white shadow-sm"
            aria-label={`Rediger opgave: ${task.title}`}
          >
            <Edit className="w-4 h-4 mr-1" aria-hidden="true" />
            Rediger
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(task.id)}
            className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 bg-white shadow-sm"
            aria-label={`Slet opgave: ${task.title}`}
          >
            <Trash2 className="w-4 h-4 mr-1" aria-hidden="true" />
            Slet
          </Button>
        </div>
      )}
    </Card>
  );
};

export default TaskCard;
